/* eslint-disable react/style-prop-object */
import React, { useEffect, useState } from "react";
import img from "../../assets/images/nr1.jpg";
import PasswordInput from "../../components/form/password-input";
import { Button, notification, Card } from "antd";
import { Link, useNavigate } from "react-router-dom";
import MTNLogo from "../../assets/images/mtnblack.png";
import AuthService, { AuthResponse } from "../../services/auth";
import DataProvider from "../../services";
import images from "../../assets/images/poweredbymtnwhite.png";
import med from "../../assets/images/md_logo.png";
import medpharmalogo from "../../assets/images/medpharma logo black.png";
import mcareblack from "../../assets/images/mcare logo (1) black.png";
import { useAppStore } from "../../store";
import { resetProfileCompletionGate } from "../../lib/profile-completion";
import EmailOrPhoneInput from "../../components/form/email-number-input";
import CustomInput from "../../components/form/custom-input";
import EmailInput from "../../components/form/email-input";
import DobInput from "../../components/form/dob-input";
import CustomPickerList from "../../components/form/custom-picker-list";
import { trackLogin } from "../../utils/analytics";
import { formatTime } from "../../utils";

type NotificationType = "success" | "info" | "warning" | "error";

export default function Login() {
  const { setToken, setUser } = useAppStore();
  const [api, contextHolder] = notification.useNotification();
  const navigation = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [contact, setContact] = useState<string>("");
  const [phonelogin, setPhonelogin] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [isOtpSent, setIsOtpSent] = useState<boolean>(false);
  const [isNewUser, setIsNewUser] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [requiresMigration, setRequiresMigration] = useState<boolean>(false);
  const [migrationEmail, setMigrationEmail] = useState<string>("");
  const [migrationStep, setMigrationStep] = useState<"otp" | "password">("otp");
  const [name, setName] = useState("");
  const [gender, setGender] = useState<string | null>(null);
  const [dob, setDob] = useState<any>(null);
  const [email, setEmail] = useState<string>("");
  const [selectedContactType, setSelectedContactType] = useState("email");
  const [otpCooldownActive, setOtpCooldownActive] = useState(false);
  const [otpCooldownTime, setOtpCooldownTime] = useState(60);

  useEffect(() => {
    if (!otpCooldownActive) return;
    const interval = setInterval(() => {
      setOtpCooldownTime((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setOtpCooldownActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [otpCooldownActive]);

  const handleToggle = (type: string) => {
    if (selectedContactType !== type) {
      setSelectedContactType(type);
      setContact("");
      setPhonelogin("");
      setIsOtpSent(false);
      setRequiresMigration(false);
    }
  };

  const openNotificationWithIcon = (
    type: NotificationType,
    title: string,
    message: string,
  ) => {
    api[type]({
      message: title,
      description: message,
    });
  };

  const handleAuthSuccess = async (authResponse: AuthResponse) => {
    await DataProvider.setToken(authResponse.token);
    await DataProvider.setRefreshToken(authResponse.refreshToken);
    setToken({ token: authResponse.token });
    // New login - the "complete your profile" nudge should be eligible to
    // show again, regardless of when it last appeared.
    resetProfileCompletionGate();

    let data;
    try {
      ({ data } = await DataProvider.getProfile());
    } catch (error) {
      // The token/refresh-token were already persisted above - if the very
      // next step (loading the profile) fails, roll them back rather than
      // leaving a valid credential stored while the UI still shows the
      // login screen ("half logged in").
      DataProvider.clearTokens();
      setToken({ token: "" });
      setLoading(false);
      openNotificationWithIcon(
        "error",
        "Sign-in incomplete",
        "We couldn't load your profile. Please try signing in again.",
      );
      return;
    }

    if (!data.fullname || !data.dateOfBirth) {
      setLoading(false);
      setIsVerified(true);
      setIsNewUser(true);
      return;
    }

    const userWith = { ...data, timenow: new Date() };
    setUser(userWith);
    setLoading(false);
    navigation("/main");
  };

  async function onSubmitemail() {
    if (contact.length < 5) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Enter a valid email.",
      );
    }

    if (!password) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Password is required.",
      );
    }

    setLoading(true);
    try {
      const emailLower = contact.trim().toLowerCase();
      const response = await AuthService.signInEmail({
        email: emailLower,
        password,
      });

      // Check if password migration is required. `requiresMigration` is a
      // literal `true` on MigrationResponse and never present on
      // AuthResponse, so this single `in` check (not `&& response.foo`,
      // which older TS doesn't narrow as reliably through) is enough for
      // TypeScript to narrow `response` to AuthResponse for the rest of
      // this function.
      if ("requiresMigration" in response) {
        setRequiresMigration(true);
        setMigrationEmail((response.email || emailLower).toLowerCase());
        setMigrationStep("otp");
        setIsOtpSent(true); // OTP is sent automatically when migration is required
        openNotificationWithIcon(
          "info",
          "Password Migration Required",
          response.message ||
            "OTP sent for password migration. Please check your email.",
        );
        setLoading(false);
        return;
      }

      // Standard login success - TS already narrows `response` to
      // AuthResponse here, since the migration branch above returns early.
      trackLogin("email", true);
      await handleAuthSuccess(response);
    } catch (error: any) {
      trackLogin("email", false);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An error occurred. Please try again.";
      openNotificationWithIcon("error", "Error", errorMessage);
      setLoading(false);
    }
  }

  async function onSubmitphonenumber() {
    if (phonelogin.length < 9) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Enter a valid phone number.",
      );
    }

    if (!isOtpSent) {
      sendOtp();
    } else {
      verifyOtp();
    }
  }

  const sendOtp = async () => {
    try {
      setLoading(true);

      await AuthService.signInPhoneNew({
        phoneNumber: phonelogin,
      });

      // Phone sign-in: no password or migration; OTP sent for verification only
      setIsOtpSent(true);
      setOtpCooldownActive(true);
      setOtpCooldownTime(60);
      setLoading(false);
      openNotificationWithIcon(
        "success",
        "OTP Sent",
        "Please check your phone for the verification code.",
      );
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to send OTP. Please try again.";
      openNotificationWithIcon("error", "Error", errorMessage);
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.length < 6) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Please enter the 6-digit OTP!",
      );
    }

    setLoading(true);
    try {
      // Use same phone format as signInWithPhone so API can match user/OTP
      const response = await AuthService.verifyOTPLogin({
        phoneNumber: phonelogin.trim(),
        otpCode: otp,
      });

      trackLogin("phone", true);
      await handleAuthSuccess(response);
    } catch (error: any) {
      trackLogin("phone", false);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Invalid OTP. Please try again.";
      openNotificationWithIcon("error", "Error", errorMessage);
      setLoading(false);
    }
  };

  const handlePasswordMigration = async () => {
    if (otp.length < 6) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Please enter the 6-digit OTP!",
      );
    }

    setLoading(true);
    try {
      // Step 1: Verify OTP for migration
      await AuthService.verifyOTPMigration({
        email: migrationEmail.trim().toLowerCase(),
        otpCode: otp,
      });

      openNotificationWithIcon(
        "success",
        "OTP Verified",
        "Please set a new password to complete your login.",
      );
      // Move to password setting step
      setMigrationStep("password");
      setIsOtpSent(false);
      setOtp("");
      setPassword(""); // Clear password field
      setLoading(false);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Invalid OTP. Please try again.";
      openNotificationWithIcon("error", "Error", errorMessage);
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (newPassword: string) => {
    if (newPassword.length < 8) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Password must be at least 8 characters.",
      );
    }

    setLoading(true);
    try {
      const response = await AuthService.setNewPassword({
        email: migrationEmail.trim().toLowerCase(),
        newPassword,
      });

      // Reset migration state
      setRequiresMigration(false);
      setMigrationStep("otp");
      setMigrationEmail("");

      trackLogin("email", true);
      await handleAuthSuccess(response);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to set password. Please try again.";
      openNotificationWithIcon("error", "Error", errorMessage);
      setLoading(false);
    }
  };

  const onNewUserSave = async () => {
    setLoading(true);
    const payload = {
      name,
      gender,
      dateOfBirth: dob,
    };
    try {
      const { data } = await DataProvider.updateProfile(payload);
      const userWith = { ...data, timenow: new Date() };
      setUser(userWith);
      setLoading(false);
      setIsVerified(false);
      setIsNewUser(false);
      navigation("/main");
    } catch (error: any) {
      openNotificationWithIcon(
        "error",
        "Error",
        "Failed, Couldn't save your data. Kindly try again.",
      );
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-full">
      {/* MOBILE VIEW */}
      <div
        className="h-screen lg:hidden flex flex-col w-full bg-cover bg-center bg-[#1A94ECB5] bg-blend-overlay relative"
        style={{ backgroundImage: `url(${img})` }}
      >
        {/* Header */}
        <div className="w-full px-4 py-4 bg-transparent">
          <div className="flex items-center justify-center gap-2">
            <Link to="/">
              <img src={MTNLogo} alt="mtn" className="h-5 rounded-l-lg" />
            </Link>
            <img
              src={medpharmalogo}
              alt="medpharma"
              className="h-6 rounded-l-lg"
            />
            <img src={mcareblack} alt="mcare" className="h-4 rounded-l-lg" />
          </div>
        </div>

        {isNewUser && isVerified ? (
          <div className="flex-1 overflow-y-auto px-4 py-6">
            <Card className="shadow-lg border-0 rounded-2xl bg-white/95 backdrop-blur-sm">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Complete Your Profile
                </h2>
                <p className="text-gray-600 text-sm">
                  Please provide some additional information to get started
                </p>
              </div>
              <div className="space-y-4">
                {/* eslint-disable-next-line react/style-prop-object */}
                <CustomInput
                  title="Full name"
                  style="h-12"
                  placeholder="Enter your full name"
                  value={name}
                  onChangeText={setName}
                />
                <DobInput dob={dob} setDob={setDob} />
                <CustomPickerList
                  title="Gender"
                  options={["male", "female"]}
                  value={gender}
                  onValueChange={setGender}
                />
                <Button
                  type="primary"
                  size="large"
                  onClick={loading ? undefined : () => onNewUserSave()}
                  className="bg-[#1A94EC] w-full h-12 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-shadow"
                  loading={loading}
                >
                  {loading ? "Saving..." : "Complete Setup"}
                </Button>
              </div>
            </Card>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4 py-6">
            <Card className="shadow-xl border-0 rounded-3xl bg-white/95 backdrop-blur-sm">
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Welcome Back
                </h1>
                <p className="text-gray-600">
                  Sign in to continue to your account
                </p>
              </div>

              {/* Login Method Toggle */}
              <div className="flex gap-4 mb-6 p-1 bg-gray-100 rounded-lg">
                <button
                  type="button"
                  onClick={() => handleToggle("email")}
                  className={`flex-1 py-2.5 px-4 rounded-md text-sm font-semibold transition-all duration-200 ${
                    selectedContactType === "email"
                      ? "bg-white text-[#1A94EC] shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => handleToggle("phone")}
                  className={`flex-1 py-2.5 px-4 rounded-md text-sm font-semibold transition-all duration-200 ${
                    selectedContactType === "phone"
                      ? "bg-white text-[#1A94EC] shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Phone
                </button>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                {selectedContactType === "email" ? (
                  <EmailOrPhoneInput
                    contact={contact}
                    setContact={setContact}
                    placeholder="Enter your email"
                    maxLength={50}
                  />
                ) : (
                  <EmailOrPhoneInput
                    contact={phonelogin}
                    setContact={setPhonelogin}
                    placeholder="Enter your phone number"
                    maxLength={15}
                  />
                )}

                {requiresMigration ? (
                  <>
                    {!migrationEmail ? (
                      <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800 font-medium mb-1">
                          Password migration is for email only
                        </p>
                        <p className="text-xs text-amber-700 mb-3">
                          To complete password migration, please sign in with
                          your email address using the Email tab above.
                        </p>
                        <Button
                          type="primary"
                          size="large"
                          onClick={() => {
                            handleToggle("email");
                            setRequiresMigration(false);
                            setMigrationEmail("");
                            setMigrationStep("otp");
                          }}
                          className="bg-[#1A94EC] w-full h-11"
                        >
                          Switch to Email
                        </Button>
                      </div>
                    ) : migrationStep === "otp" ? (
                      <>
                        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800 font-medium mb-1">
                            Password Migration Required
                          </p>
                          <p className="text-xs text-blue-600">
                            An OTP has been sent to your email{" "}
                            <span className="font-medium">
                              {migrationEmail}
                            </span>
                            . Please verify to continue.
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Verification Code
                          </label>
                          <input
                            maxLength={6}
                            className="w-full h-12 px-4 rounded-lg border border-gray-300 focus:border-[#1A94EC] focus:ring-2 focus:ring-[#1A94EC]/20 outline-none transition-all text-center text-lg tracking-widest"
                            type="text"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            placeholder="000000"
                          />
                        </div>
                        <Button
                          type="primary"
                          size="large"
                          onClick={
                            loading
                              ? undefined
                              : () => handlePasswordMigration()
                          }
                          className="bg-[#1A94EC] w-full h-12 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
                          loading={loading}
                        >
                          Verify OTP
                        </Button>
                        <div className="flex gap-2 mt-3">
                          <Button
                            onClick={() => {
                              setRequiresMigration(false);
                              setMigrationStep("otp");
                              setIsOtpSent(false);
                              setOtp("");
                              setMigrationEmail("");
                            }}
                            size="large"
                            className="flex-1 h-11"
                          >
                            Cancel
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-800 font-medium mb-1">
                            OTP verified successfully
                          </p>
                          <p className="text-xs text-green-600">
                            Please set a new password to complete your login.
                          </p>
                        </div>
                        <PasswordInput
                          required
                          password={password}
                          setPassword={setPassword}
                          placeholder="Enter new password (min 8 characters)"
                        />
                        <div className="text-xs text-gray-500 mb-2">
                          Password must be at least 8 characters with uppercase,
                          lowercase, number, and special character.
                        </div>
                        <Button
                          type="primary"
                          size="large"
                          onClick={
                            loading
                              ? undefined
                              : () => handleSetNewPassword(password)
                          }
                          className="bg-[#1A94EC] w-full h-12 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
                          loading={loading}
                        >
                          {loading
                            ? "Setting Password..."
                            : "Set Password & Sign In"}
                        </Button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {isOtpSent ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Verification Code
                        </label>
                        <input
                          maxLength={6}
                          className="w-full h-12 px-4 rounded-lg border border-gray-300 focus:border-[#1A94EC] focus:ring-2 focus:ring-[#1A94EC]/20 outline-none transition-all text-center text-lg tracking-widest"
                          type="text"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          placeholder="000000"
                        />
                        <div className="flex gap-2 mt-3">
                          <Button
                            disabled={otpCooldownActive}
                            onClick={sendOtp}
                            size="large"
                            className="flex-1 h-11"
                          >
                            {otpCooldownActive
                              ? `Resend in ${formatTime(otpCooldownTime)}`
                              : "Resend"}
                          </Button>
                          <Button
                            onClick={() => {
                              setIsOtpSent(false);
                              setOtp("");
                            }}
                            size="large"
                            className="flex-1 h-11"
                          >
                            Change
                          </Button>
                        </div>
                      </div>
                    ) : (
                      selectedContactType === "email" && (
                        <PasswordInput
                          required
                          forgot
                          password={password}
                          setPassword={setPassword}
                          placeholder="Enter your password"
                        />
                      )
                    )}

                    <Button
                      type="primary"
                      size="large"
                      onClick={
                        loading
                          ? undefined
                          : selectedContactType === "email"
                            ? () => onSubmitemail()
                            : () => onSubmitphonenumber()
                      }
                      className="bg-[#1A94EC] w-full h-12 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
                      loading={loading}
                    >
                      {loading
                        ? "Processing..."
                        : isOtpSent
                          ? "Verify OTP"
                          : "Sign In"}
                    </Button>
                  </>
                )}
              </div>

              {/* Footer Links */}
              <div className="mt-6 text-center">
                <p className="text-gray-600 text-sm">
                  Don't have an account?{" "}
                  <Link
                    to="/sign-up"
                    className="text-[#1A94EC] font-semibold hover:underline"
                  >
                    Create account
                  </Link>
                </p>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* DESKTOP VIEW */}
      <div className="h-screen w-full lg:flex hidden">
        {/* Left Side - Branding */}
        {/* eslint-disable-next-line react/forbid-dom-props */}
        <div
          className="h-full w-[45%] bg-center relative flex flex-col justify-between p-8 bg-cover bg-[#1A94ECB5] bg-blend-overlay"
          style={{ backgroundImage: `url(${img})` }}
        >
          {/* <div className="absolute inset-0 bg-cover bg-[#1A94ECB5] bg-blend-overlay"></div> */}
          <div className="relative z-10">
            <Link to="/" className="inline-block">
              <img src={images} className="h-16" alt="mcare" />
            </Link>
          </div>
          <div className="relative z-10 text-white">
            <h1 className="text-7xl font-bold mb-4 leading-tight">
              Welcome Back
            </h1>
            <p className="text-2xl font-light opacity-90">
              Your health, your way
            </p>
          </div>
          <div className="relative z-10 flex gap-4">
            <img src={med} className="h-10" alt="med" />
            <img src={MTNLogo} className="h-10" alt="mtn" />
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="h-full w-[55%] flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50 p-12 overflow-y-auto">
          {isNewUser && isVerified ? (
            <div className="w-full max-w-lg">
              <Card className="shadow-2xl border-0 rounded-3xl p-10 bg-white/95 backdrop-blur-sm">
                <div className="mb-8">
                  <h2 className="text-4xl font-bold text-gray-900 mb-3">
                    Complete Your Profile
                  </h2>
                  <p className="text-gray-600 text-base">
                    Please provide some additional information to get started
                  </p>
                </div>
                <div className="space-y-6">
                  {/* eslint-disable-next-line react/style-prop-object */}
                  <CustomInput
                    title="Full name"
                    style="h-12"
                    placeholder="Enter your full name"
                    value={name}
                    onChangeText={setName}
                  />
                  <EmailInput
                    title="Email (optional)"
                    email={email}
                    setEmail={setEmail}
                    placeholder="Enter your email address"
                  />
                  <DobInput dob={dob} setDob={setDob} />
                  <CustomPickerList
                    title="Gender"
                    options={["male", "female"]}
                    value={gender}
                    onValueChange={setGender}
                  />
                  <Button
                    type="primary"
                    size="large"
                    onClick={loading ? undefined : () => onNewUserSave()}
                    className="bg-[#1A94EC] w-full h-14 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all mt-6 text-base"
                    loading={loading}
                  >
                    {loading ? "Saving..." : "Complete Setup"}
                  </Button>
                </div>
              </Card>
            </div>
          ) : (
            <div className="w-full max-w-lg">
              <div className="bg-white/95 backdrop-blur-sm shadow-2xl border-0 rounded-3xl p-10">
                <div className="mb-10">
                  <h1 className="text-5xl font-bold text-gray-900 mb-3 leading-tight">
                    Welcome Back
                  </h1>
                  <p className="text-gray-600 text-lg">
                    Sign in to access your account and continue your journey
                  </p>
                </div>

                {/* Login Method Toggle */}
                <div className="flex gap-2 mb-8 p-1 bg-gradient-to-r from-gray-100 to-gray-50 rounded-2xl border border-gray-200">
                  <button
                    type="button"
                    onClick={() => handleToggle("email")}
                    className={`flex-1 py-4 px-6 rounded-xl text-base font-semibold transition-all duration-300 ${
                      selectedContactType === "email"
                        ? "bg-white text-[#1A94EC] shadow-lg transform scale-[1.02]"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggle("phone")}
                    className={`flex-1 py-4 px-6 rounded-xl text-base font-semibold transition-all duration-300 ${
                      selectedContactType === "phone"
                        ? "bg-white text-[#1A94EC] shadow-lg transform scale-[1.02]"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    Phone
                  </button>
                </div>

                {/* Form Fields */}
                <div className="space-y-6">
                  {selectedContactType === "email" ? (
                    <EmailOrPhoneInput
                      contact={contact}
                      setContact={setContact}
                      placeholder="Enter your email address"
                      maxLength={50}
                    />
                  ) : (
                    <EmailOrPhoneInput
                      contact={phonelogin}
                      setContact={setPhonelogin}
                      placeholder="Enter your phone number"
                      maxLength={15}
                    />
                  )}

                  {requiresMigration ? (
                    <>
                      {migrationStep === "otp" ? (
                        <>
                          <div className="mb-6 p-5 bg-blue-50 border border-blue-200 rounded-xl">
                            <p className="text-base text-blue-800 font-semibold mb-2">
                              Password Migration Required
                            </p>
                            <p className="text-sm text-blue-600">
                              An OTP has been sent to{" "}
                              {migrationEmail || "your email/phone"}. Please
                              verify to continue.
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Verification Code
                            </label>
                            <input
                              maxLength={6}
                              className="w-full h-14 px-4 rounded-xl border-2 border-gray-200 focus:border-[#1A94EC] focus:ring-4 focus:ring-[#1A94EC]/10 outline-none transition-all text-center text-xl tracking-[0.5em] font-semibold"
                              type="text"
                              value={otp}
                              onChange={(e) => setOtp(e.target.value)}
                              placeholder="000000"
                            />
                          </div>
                          <Button
                            type="primary"
                            size="large"
                            onClick={
                              loading
                                ? undefined
                                : () => handlePasswordMigration()
                            }
                            className="bg-gradient-to-r from-[#1A94EC] to-[#0d7bc4] w-full h-14 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-[#0d7bc4] hover:to-[#1A94EC] transition-all text-base mt-4"
                            loading={loading}
                          >
                            Verify OTP
                          </Button>
                          <Button
                            onClick={() => {
                              setRequiresMigration(false);
                              setMigrationStep("otp");
                              setIsOtpSent(false);
                              setOtp("");
                              setMigrationEmail("");
                            }}
                            size="large"
                            className="w-full h-12 rounded-xl mt-3"
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <div className="mb-6 p-5 bg-green-50 border border-green-200 rounded-xl">
                            <p className="text-base text-green-800 font-semibold mb-2">
                              OTP Verified Successfully
                            </p>
                            <p className="text-sm text-green-600">
                              Please set a new password to complete your login.
                            </p>
                          </div>
                          <PasswordInput
                            required
                            password={password}
                            setPassword={setPassword}
                            placeholder="Enter new password (min 8 characters)"
                          />
                          <div className="text-sm text-gray-500 mb-4">
                            Password must be at least 8 characters with
                            uppercase, lowercase, number, and special character.
                          </div>
                          <Button
                            type="primary"
                            size="large"
                            onClick={
                              loading
                                ? undefined
                                : () => handleSetNewPassword(password)
                            }
                            className="bg-gradient-to-r from-[#1A94EC] to-[#0d7bc4] w-full h-14 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-[#0d7bc4] hover:to-[#1A94EC] transition-all text-base"
                            loading={loading}
                          >
                            {loading
                              ? "Setting Password..."
                              : "Set Password & Sign In"}
                          </Button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {isOtpSent ? (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Verification Code
                          </label>
                          <input
                            maxLength={6}
                            className="w-full h-14 px-4 rounded-xl border-2 border-gray-200 focus:border-[#1A94EC] focus:ring-4 focus:ring-[#1A94EC]/10 outline-none transition-all text-center text-xl tracking-[0.5em] font-semibold"
                            type="text"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            placeholder="000000"
                          />
                          <div className="flex gap-3 mt-4">
                            <Button
                              disabled={otpCooldownActive}
                              onClick={sendOtp}
                              size="large"
                              className="flex-1 h-12 rounded-xl"
                            >
                              {otpCooldownActive
                                ? `Resend in ${formatTime(otpCooldownTime)}`
                                : "Resend Code"}
                            </Button>
                            <Button
                              onClick={() => {
                                setIsOtpSent(false);
                                setOtp("");
                              }}
                              size="large"
                              className="flex-1 h-12 rounded-xl"
                            >
                              Change Number
                            </Button>
                          </div>
                        </div>
                      ) : (
                        selectedContactType === "email" && (
                          <PasswordInput
                            required
                            forgot
                            password={password}
                            setPassword={setPassword}
                            placeholder="Enter your password"
                          />
                        )
                      )}

                      <Button
                        type="primary"
                        size="large"
                        onClick={
                          selectedContactType === "email"
                            ? () => onSubmitemail()
                            : () => onSubmitphonenumber()
                        }
                        className="bg-gradient-to-r from-[#1A94EC] to-[#0d7bc4] w-full h-14 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-[#0d7bc4] hover:to-[#1A94EC] transition-all text-base mt-2"
                        loading={loading}
                      >
                        {loading
                          ? "Processing..."
                          : isOtpSent
                            ? "Verify OTP"
                            : "Sign In"}
                      </Button>
                    </>
                  )}
                </div>

                {/* Footer Links */}
                <div className="mt-10 text-center">
                  <p className="text-gray-600 text-base">
                    Don't have an account?{" "}
                    <Link
                      to="/sign-up"
                      className="text-[#1A94EC] font-semibold hover:text-[#0d7bc4] hover:underline transition-all"
                    >
                      Create account
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {contextHolder}
    </div>
  );
}
